<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-brand-configuration-usage" v-loading="loading">
    <div class="title-btns-section"> 
      <div class="title">Usage alerts</div>
      <div class="update-btns">
        <!-- <div class="update-cancel-btn" @click="controller.appVue.handleAppModalClose">Cancel</div> -->
        <button :class="(hasError||!hasNoSavedChanges?'update-save-btn disabled':'update-save-btn')"  :disabled="hasError||!hasNoSavedChanges" @click="updateUsage">Update</button>
      </div>
    </div>
    <div class="btn-section">
      <!-- <div class="clear-all-btn" :class="{'disabled':disableClearAllBtn}" @click="handleClearAllTargets"><span>Clear all values</span></div> -->
    </div>
    <div class="title-description-content">
      <div class="title-description"><span>Set up notifications for your organization's Analytics processing to avoid overage charges. You will receive an alert each day based on your settings below.</span></div>
      <div v-if="!loading&&hasUsages" class="usages-status"><span class="has-alerts"></span>Alerts setup</div>
      <div v-else-if="!loading&&!hasUsages" class="usages-status"><span class="has-no-alerts"></span>No alerts setup</div>
    </div>
    
    <div class="content-section">
      <div class="section-title">Monthly billing cycle</div>
      <div class="billing-cycle block" v-for="(item, key) in billingCycle" :key="key">
        <div v-if="key==='billingStartDate'">
          <div class="item-title">Your monthly usage begins on your contract start date and renews on that same day each month. For example, if your contract starts on the 15th, your usage resets on the 15th of every month.</div>
          <span class="demonstration">Select your contract start date:</span>
          <el-date-picker
            :class="{'no-value':item&&item.value==null}"
            v-model="item.value"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder="Select date">
          </el-date-picker>
        </div>
      </div>
    </div>
    <div class="content-section">
      <div class="section-title">Monthly usage limit</div>
      <div class="item-title">Your monthly usage limit is calculated by multiplying your organization's number of Analytics Enterprise and Analytics Enterprise Plus licenses by the number of interactions included in each license.</div>
      <div v-for="(item, key) in usageLimit" :key="key"> 
        <div class="usages-section" :class="key">
          <div v-if="key==='contractLicenseNumber'" class="item-title">
            <div class="item-subtitle">Total contracted licenses</div>
            {{usagesMap[key].description}}</div>
          <div v-if="key==='numberOfInteractions'" class="item-title">
            <div class="item-subtitle">Total Interactions included</div>
            {{usagesMap[key].description}}
          </div>
          <div v-if="key==='usageAlertPercentage'" class="item-title">
            <div class="item-subtitle">Alert threshold</div>
            {{usagesMap[key].description}}
          </div>
          <vue-numeric v-if="item.type==='integer'" v-bind:minus="false" separator="," v-bind:precision="0" v-bind:min="0" :class="{'no-value':item&&item.value==''}" v-model="item.value" :placeholder="usagesMap[key].placeholder"></vue-numeric> 
          <vue-numeric v-else-if="item.type==='percentage'" v-bind:minus="false" separator="," v-bind:precision="0" v-bind:min="0" v-bind:max="100" :class="{key, 'no-value':item&&item.value==''}" v-model="item.value" :placeholder="usagesMap[key].placeholder"></vue-numeric>
          <vue-numeric v-else v-bind:minus="false" separator="," v-bind:precision="2" v-bind:min="0" :class="{key, 'no-value':item&&item.value==''}" v-model="item.value" :placeholder="usagesMap[key].placeholder"></vue-numeric> 
          <span v-if="item.type==='percentage'" class="percentage-sign">%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import VueNumeric from 'vue-numeric'

  export default {
    name: 'wy-brand-configuration-usage',
    components: {
      VueNumeric
    },
    props:  ['controller'],
    
    data() {
      return {
        module: this,
        loading: false,
        companyConfigSettings: {},
        configurationData: {},
        configurationDataClone: {},
        usagesMap: {
          contractLicenseNumber: {
            description: 'Enter the number of Analytics Enterprise and Analytics Enterprise Plus licenses your organization has purchased.',
            placeholder: 'Number of contracted licenses'
          },
          numberOfInteractions: {
            description: 'Enter the number of interactions included with each license.',
            placeholder: 'Number of interactions'
          },
          usageAlertPercentage: {
            description: 'Set the usage threshold for the alert. You will receive an alert when your monthly usage reaches the percentage of total interactions specified below.',
            placeholder: 'Percentage '
          }
        },
        billingDate: '',
        tableData: [],
        tableDataClone: [],
        originCompanySettingData: null,
        disableClearAllBtn: false
      };
    },


    computed: {
      ...mapGetters(['dataModel', 'isRoleAllow']),

      hasUsages() {
        return this.companyConfigSettings && this.companyConfigSettings.settings
      },

      hasError() {
        let hasError = false;
        Object.keys(this.configurationData).forEach(key => {
          if(this.configurationData[key].value === '' || this.configurationData[key].value == null) {
            hasError = true
          }
        })
        return hasError;
      },

      hasNoSavedChanges() {
        return (JSON.stringify(this.configurationData) !== JSON.stringify(this.configurationDataClone))
      },

      billingCycle() {
        let list = {};
        Object.keys(this.configurationData).forEach((key) => {
          if(this.configurationData[key].section === 'billingCycle') {
            list[key] = this.configurationData[key]
          }
        })
        return list
      },

      usageLimit() {
        let list = {};
        Object.keys(this.configurationData).forEach((key) => {
          if(this.configurationData[key].section === 'usageLimit') {
            list[key] = this.configurationData[key]
          }
        })
        return list
      },
    },

    methods: {
      ...mapActions(['updateCompanyConfigSettings', 'updateCompanyConfigSettings', 'fetchCompanyConfigSettings']),


      handleDiscardUnsavedChanges() {
        this.controller.hasError = false;
        this.configurationData = JSON.parse(JSON.stringify(this.configurationDataClone));
      },

      async updateUsage() {
        let updateData = {}
        // updateData.usages = null
        updateData.key = 'usage';
        updateData.settings = this.configurationData;
        // updateData.settings = {}
        this.loading = true;
        let success = await this.updateCompanyConfigSettings(updateData);
        // let success = true;
        if(success) {
          this.loading = false;
          // this.controller.hasNoSavedChanges = false;
          this.controller.isDashBoardModified = true;
          this.configurationDataClone = JSON.parse(JSON.stringify(this.configurationData));
          this.$message({
            showClose: true,
            message: this.$t("configurations.usageUpdateSuccess"),
            customClass: 'configuration-success-message',
            duration: 5000,
            type: 'success'
          });
        }
        else {
          this.loading = false;
          this.$message({
            showClose: true,
            message: this.$t("configurations.usageUpdateFailed"),
            customClass: 'configuration-failed-message',
            duration: 5000,
            type: 'error'
          });
        }
      },

      handleSaveUnchanges(newTab, handleAppModalClose) {
        this.controller.$refs.updateDialog.title = `Review changes`;
        this.controller.$refs.updateDialog.content="There are unsaved changes. Do you want to discard or save them before leaving?" 
        this.controller.$refs.updateDialog.open();
        if(this.hasError) {
          this.controller.$refs.updateDialog.disableSaveBtn = true;
        }
        else {
          this.controller.$refs.updateDialog.disableSaveBtn = false;
        }
        this.controller.$refs.updateDialog.confirmButtonCallback = async () => {
          await this.updateUsage()
          if(handleAppModalClose) {
            handleAppModalClose();
          }
          else {
            this.controller.handleTabClick({name: newTab})
            this.controller.activeTab = newTab
          }
        }
        this.controller.$refs.updateDialog.discardButtonCallback = async () => {
          this.handleDiscardUnsavedChanges()
          if(handleAppModalClose) {
            handleAppModalClose();
          }
          else {
            this.controller.handleTabClick({name: newTab})
            this.controller.activeTab = newTab
          }
        }
      },

      async prepareData() {
        this.loading = true;
        this.companyConfigSettings = await this.fetchCompanyConfigSettings({key: 'usage'})
        this.originCompanySettingData = JSON.parse(JSON.stringify(this.companyConfigSettings));
        if(this.companyConfigSettings && this.companyConfigSettings.settings) {
          this.configurationData = this.companyConfigSettings.settings;
        }
        if(!this.configurationData || Object.keys(this.configurationData).length == 0) {
          this.configurationData = {
            "billingStartDate": {
              "prefix": "",
              "suffix": "",
              "value": null,
              "type": 'string',
              'section': 'billingCycle'
            },
            "contractLicenseNumber": {
              "prefix": "",
              "suffix": "",
              "value": '',
              "type": 'integer',
              'section': 'usageLimit'
            },
            "numberOfInteractions": {
              "prefix": "",
              "suffix": "",
              "value": '',
              "type": 'integer',
              'section': 'usageLimit'

            },
            "usageAlertPercentage": {
              "prefix": "",
              "suffix": "%",
              "value": '',
              "type": 'percentage',
              'section': 'usageLimit'
            }
          }
        }
        this.configurationDataClone = JSON.parse(JSON.stringify(this.configurationData));
        this.loading = false;
        setTimeout(() => {
          if(document.querySelectorAll('.usages-section') && document.querySelectorAll('.usages-section').length > 0) {
            document.querySelectorAll('.usages-section input').forEach(el => {
              el.addEventListener('keypress',(e)=>{
                var keyCode = e.keyCode || e.which;
                if ((keyCode < 48 || keyCode > 57) || keyCode == 46) {
                  e.preventDefault();
                  return false;
                }
              })
            })
          }
        }, 100)
      }
    },

    async created() {
      this.prepareData();
    },
  };
</script>

<style lang="scss">
  .wy-brand-configuration-usage {
    min-width: 530px;
    height: inherit;

    .usages-status {
      position: relative;
      top: -3px;
      right: -5px;
      margin-top: 0px;
      display: block;
      font-size: 14px;
      font-weight: 400;
      width: 128px;
    }

    .has-alerts {
      background-image: url('../../assets/icons/icon_has-alerts.svg') !important;
      height: 16px;
      width: 16px;
      background-size: 15px;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      display: inline-block;
      position: relative;
      top: 4px;
      left: -6px;
    }
    .has-no-alerts {
      background-image: url('../../assets/icons/icon_no-alerts.svg') !important;
      height: 16px;
      width: 16px;
      background-size: 15px;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      display: inline-block;
      position: relative;
      top: 4px;
      left: -6px;
    }

    .content-section {
      height: fit-content;
      border-bottom: 1px #CCC solid;
      margin: 0px 20px 0px 20px;
      padding-top: 25px;
      padding-bottom: 25px;

      .billing-cycle {
        input.el-input__inner {
          border-width: 1px !important;
        }
        .el-input--prefix .el-input__inner {
          padding-left: 10px;
          border-radius: 5px;
          border: 1px solid #106fbc;
          background-color: #e8f1ff;
        }
        .el-input--prefix.no-value .el-input__inner {
          background-color: #fff4f4;
          border: 1px solid #ebafba;
        }
        .el-input__prefix {
          right: 5px !important;
          left: unset;
        }

        .el-input__suffix {
          right: 25px !important;
        }

        .item-title, .demonstration{
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          color: #2A2E32;
          padding-bottom: 15px;
          padding-top: 15px;
          padding-right: 10px;
        }
      }
      
      .section-title {
        font-size: 16px;
        font-style: normal;
        color: #2A2E32;
        font-weight: 500;
        padding-bottom: 5px;
      }
      .usages-section {
        .item-title {
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          color: #2A2E32;
          padding-bottom: 15px;
          padding-top: 15px;
          .item-subtitle {
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            padding-top: 5px;
            padding-bottom: 15px;
          }
        }
        input.no-value {
          background-color: #fff4f4;
          border: 1px solid #ebafba;
        }
        input {
          // padding: 5px 20px 5px 20px;
          height: 30px;
          width: 216px;
          border: 1px solid #106fbc;
          border-radius: 5px;
          text-align: center;
          background-color: #e8f1ff;
          outline: none;
          padding-left: 10px;
        }
        input::placeholder {
          text-align: left;
        }
        input:-ms-input-placeholder {
          text-align: left;
        }
        .percentage-sign {
          padding-left: 20px;
        }
      }
      .usages-section.contractLicenseNumber {
        input {
          width: 216px;
        }
      }
      .usages-section.numberOfInteractions {
        input {
          width: 216px;
        }
      }
      .usages-section.usageAlertPercentage {
        input {
          width: 102px;
        }
      }
    }
    .wy-genaric-table .wy-table-wrapper {
      border-radius: 0px !important;
      height: calc(100% - 75px);
      // padding-right: 10px;

      // &::-webkit-scrollbar {
      //   background-color: white !important;
      //   width: 10px !important;
      // }
      // &::-webkit-scrollbar-thumb{
      //   // background: #CAD2DA !important;
      //   background: #c5c5c7 !important;
      //   border-radius: 50px !important; 
      // } 
    }
    .wy-genaric-table .wy-table-fixed tbody tr td:first-child {
      background-color: #FFFF;
    }
    .wy-genaric-table .wy-table-main thead tr {
      width: 100%;
      width: calc(100vw - 372px);
    }
    .wy-table-main thead {
      background-color: #FFF;
      border: 1px solid #CAD2DA;
      width: calc(100vw - 372px);
    }
    .wy-table-main thead th {
      background-color: #FFF;
      text-align: left !important;
    }
    .wy-genaric-table .wy-table-main tbody {
      
      // height: calc( 100vh - 488px);
      height: calc( 100vh - 355px);
      overflow: hidden;
      width: calc(100vw - 372px);
      border-right: 1px solid #CAD2DA;
      border-left: 1px solid #CAD2DA;
      border-bottom: 1px solid #CAD2DA;
    }
    .wy-genaric-table tbody tr td {
      border-bottom: 0px solid #eee;
      div {
        // margin-left: 16px;
        justify-content: left;
      }
    }
    .title-btns-section {
      display: inline-flex;
      width: 100%;
      margin-bottom: 10px;
      .title {
        line-height: 2;
        font-size: 16px;
        font-weight: 700;
      }
      .update-btns {
        display: inline-flex;
        margin-left: auto;
      }
      .update-cancel-btn {
        color: #202223 !important;
        font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
        font-size: 15px;
        font-weight: 400;
        padding: 8px 16px 8px 16px;
        display: flex;
        border-radius: 5px;
        height: fit-content;
        width: fit-content;
        border: 1px solid #CAD2DA;
        cursor: pointer;
        background: white;
        margin-right: 15px;
        align-items: center;
        justify-content: center;
        &:hover{
          background: #F4F6F8;
        }
      }
      .update-save-btn {
        width: fit-content;
        height: fit-content;
        padding: 8px 16px 8px 16px;
        font-size: 15px;
        cursor: pointer;
        background: var(--site-button-primary-btn-background-primary-color);
        color: var(--site-button-primary-btn-text-primary-color) !important;
        border: 1px solid var(--site-button-primary-btn-hover-border-primary-color);
        font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
        border-radius: 5px;
        // margin-right: 15px;
        align-items: center;
        justify-content: center;
      }
      .update-save-btn:hover {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        background-color: var(--site-button-primary-btn-hover-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn:active {
        background-color: var(--site-button-primary-btn-pressed-background-primary-color) !important;
      }
      .update-save-btn:focus {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn.disabled {
        background: var(--site-button-primary-btn-disabled-background-primary-color);
        cursor: not-allowed;
      }
      .update-save-btn.disabled:hover {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn.disabled:active {
        background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
      }
      .update-save-btn.disabled:focus {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .intent-action-dropdown-save-btn:disabled{
        background: #C0C4CC;
        cursor: not-allowed;
      }
    }
    .btn-section {
      display: flex;
      float: right;
      // width: 425px;
      .clear-all-btn {
        font-size: 12px;
        width: 105px;
        text-align: right;
        line-height: 1;
        color: #106FBC;
        cursor: pointer;
      }
      .clear-all-btn.disabled{
        pointer-events: none;
        color: #CCC;
      }
      .reset-all-btn {
        font-size: 12px;
        width: 142px;
        line-height: 1;
        color: #106FBC;
        cursor: pointer;
      }
    }
    .title-description-content {
      font-size: 14px;
      font-weight: 400;
      padding-top: 8px;
      padding-bottom: 30px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      .title-description {
        width: calc(100% - 100px);
      }
    }
    // .el-loading-mask {
    //   height: calc(100vh - 400px);
    // }
  }
  .wy-brand-configuration-usage > .content-section {
    border-top: 1px #CCC solid;
    max-width: 800px;
    .item-title {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      color: #2A2E32;
      padding-bottom: 0px;
      padding-top: 15px;
      padding-right: 10px;
    }
  }

  .wy-brand-configuration-usage > .content-section ~ .content-section {
    border-top: 0px;
  }

  .configuration-success-message {
    background-color: #D9EEDF;
    border-color: #86D2AE;
    .el-icon-success {
      color: #186731;
    }
    .el-message__content {
      color: var(--site-font-secondary-color);
    }
  }
</style>